import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '../../../lib/utils';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraText } from '../ensigns/typography';
import { chartToneVar, SpecraChartFrame } from '../lieutenants/chart-frame';
import { SpecraTooltip, SpecraTooltipProvider } from '../lieutenants/tooltip';
/** Each code's tile: [row, column] on an 8 by 11 grid. */ export const US_TILE_GRID = {
    AK: [
        0,
        0
    ],
    ME: [
        0,
        10
    ],
    WI: [
        1,
        5
    ],
    VT: [
        1,
        9
    ],
    NH: [
        1,
        10
    ],
    WA: [
        2,
        0
    ],
    ID: [
        2,
        1
    ],
    MT: [
        2,
        2
    ],
    ND: [
        2,
        3
    ],
    MN: [
        2,
        4
    ],
    IL: [
        2,
        5
    ],
    MI: [
        2,
        6
    ],
    NY: [
        2,
        8
    ],
    MA: [
        2,
        9
    ],
    OR: [
        3,
        0
    ],
    NV: [
        3,
        1
    ],
    WY: [
        3,
        2
    ],
    SD: [
        3,
        3
    ],
    IA: [
        3,
        4
    ],
    IN: [
        3,
        5
    ],
    OH: [
        3,
        6
    ],
    PA: [
        3,
        7
    ],
    NJ: [
        3,
        8
    ],
    CT: [
        3,
        9
    ],
    RI: [
        3,
        10
    ],
    CA: [
        4,
        0
    ],
    UT: [
        4,
        1
    ],
    CO: [
        4,
        2
    ],
    NE: [
        4,
        3
    ],
    MO: [
        4,
        4
    ],
    KY: [
        4,
        5
    ],
    WV: [
        4,
        6
    ],
    VA: [
        4,
        7
    ],
    MD: [
        4,
        8
    ],
    DE: [
        4,
        9
    ],
    AZ: [
        5,
        1
    ],
    NM: [
        5,
        2
    ],
    KS: [
        5,
        3
    ],
    AR: [
        5,
        4
    ],
    TN: [
        5,
        5
    ],
    NC: [
        5,
        6
    ],
    SC: [
        5,
        7
    ],
    DC: [
        5,
        8
    ],
    OK: [
        6,
        3
    ],
    LA: [
        6,
        4
    ],
    MS: [
        6,
        5
    ],
    AL: [
        6,
        6
    ],
    GA: [
        6,
        7
    ],
    HI: [
        7,
        0
    ],
    TX: [
        7,
        3
    ],
    FL: [
        7,
        8
    ],
    PR: [
        7,
        10
    ]
};
const STATE_NAMES = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
    PR: 'Puerto Rico'
};
/** Whether a code has a tile (the 50 states, DC and PR), in any case. */ const TILE_CODES = new Set(Object.keys(US_TILE_GRID));
export function isUsStateCode(code) {
    return TILE_CODES.has(code.trim().toUpperCase());
}
const ROWS = 8;
const COLS = 11;
const HEAT = [
    'var(--chart-heat-0)',
    'var(--chart-heat-1)',
    'var(--chart-heat-2)',
    'var(--chart-heat-3)',
    'var(--chart-heat-4)'
];
const grouped = (v)=>v.toLocaleString('en-US');
/**
 * The quantile breaks of the values: up to four bins, each the next quarter
 * of the states by value. Returns each bin's [low, high].
 */ export function tileBins(values) {
    const sorted = [
        ...values
    ].sort((a, b)=>a - b);
    if (!sorted.length) return [];
    const bins = [];
    const k = Math.min(4, sorted.length);
    for(let i = 0; i < k; i++){
        const lo = sorted[Math.floor(i * sorted.length / k)];
        const hi = sorted[Math.floor((i + 1) * sorted.length / k) - 1];
        const prev = bins[bins.length - 1];
        if (prev && lo <= prev[1]) prev[1] = Math.max(prev[1], hi);
        else bins.push([
            lo,
            hi
        ]);
    }
    return bins;
}
/** The ramp step (1–4) of a value given the bins; spread over the ramp when fewer than four. */ function levelOf(v, bins) {
    let i = bins.findIndex(([, hi])=>v <= hi);
    if (i < 0) i = bins.length - 1;
    if (bins.length <= 1) return 4;
    return 1 + Math.round(i * 3 / (bins.length - 1));
}
/** A value per US state as an equal-tile map with a table of the same numbers. */ export function SpecraTileMap({ states, format = grouped, valueLabel = 'Value', source, title, description, insight, className, 'data-testid': testId }) {
    const byCode = new Map();
    const unknown = [];
    for (const s of states){
        const code = s.code.trim().toUpperCase();
        if (isUsStateCode(code)) byCode.set(code, {
            ...s,
            code
        });
        else unknown.push(s.code);
    }
    const values = [
        ...byCode.values()
    ].map((s)=>s.value).filter((v)=>typeof v === 'number' && Number.isFinite(v));
    const bins = tileBins(values);
    const showPR = byCode.has('PR');
    const table = {
        columns: [
            {
                key: 'state',
                label: 'State'
            },
            {
                key: 'value',
                label: valueLabel,
                numeric: true
            },
            {
                key: 'note',
                label: 'Note'
            }
        ],
        rows: [
            ...byCode.values()
        ].sort((a, b)=>(b.value ?? Number.NEGATIVE_INFINITY) - (a.value ?? Number.NEGATIVE_INFINITY)).map((s)=>({
                id: s.code,
                cells: {
                    state: STATE_NAMES[s.code] ?? s.code,
                    value: typeof s.value === 'number' ? format(s.value) : '—',
                    note: s.note ?? ''
                }
            }))
    };
    const legend = bins.length ? /*#__PURE__*/ _jsxs("ul", {
        "aria-label": "Legend",
        className: "flex flex-wrap items-center gap-x-[var(--space-3)] gap-y-[var(--space-1)]",
        children: [
            bins.map(([lo, hi])=>/*#__PURE__*/ _jsxs("li", {
                    className: "flex items-center gap-[var(--space-1-5)]",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": "true",
                            className: "inline-block size-2.5 shrink-0 rounded-[var(--radius-xs)]",
                            style: {
                                background: HEAT[levelOf(hi, bins)]
                            }
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            size: "meta",
                            tone: "muted",
                            children: lo === hi ? format(lo) : `${format(lo)}–${format(hi)}`
                        })
                    ]
                }, `${lo}-${hi}`)),
            /*#__PURE__*/ _jsxs("li", {
                className: "flex items-center gap-[var(--space-1-5)]",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        "aria-hidden": "true",
                        className: "inline-block size-2.5 shrink-0 rounded-[var(--radius-xs)] border border-[color:var(--border-subtle)]",
                        style: {
                            background: HEAT[0]
                        }
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: "No data"
                    })
                ]
            })
        ]
    }) : undefined;
    const footnote = unknown.length ? `Not on the map: ${unknown.join(', ')}.` : undefined;
    return /*#__PURE__*/ _jsxs(SpecraChartFrame, {
        className: className,
        "data-testid": testId,
        description: description,
        insight: insight,
        legend: legend,
        table: table,
        title: title,
        children: [
            /*#__PURE__*/ _jsx(SpecraTooltipProvider, {
                delayDuration: 100,
                children: /*#__PURE__*/ _jsx("div", {
                    className: "grid w-full gap-[var(--space-0-5)]",
                    "data-tile-map": "",
                    style: {
                        gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`,
                        gridTemplateRows: `repeat(${ROWS}, auto)`
                    },
                    children: Object.entries(US_TILE_GRID).map(([code, [row, col]])=>{
                        if (code === 'PR' && !showPR) return null;
                        const s = byCode.get(code);
                        const hasValue = typeof s?.value === 'number' && Number.isFinite(s.value);
                        const level = hasValue ? levelOf(s?.value, bins) : 0;
                        const fill = s?.tone ? chartToneVar[s.tone] : HEAT[level];
                        const strongInk = s?.tone ? s.tone !== 'pending' && s.tone !== 'warn' : level >= 3;
                        const name = STATE_NAMES[code] ?? code;
                        const valueText = hasValue ? format(s?.value) : 'No data';
                        return /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: /*#__PURE__*/ _jsxs("span", {
                                className: "block",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "block font-semibold",
                                        children: name
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "block",
                                        children: [
                                            valueLabel,
                                            ": ",
                                            valueText
                                        ]
                                    }),
                                    s?.note ? /*#__PURE__*/ _jsx("span", {
                                        className: "block",
                                        children: s.note
                                    }) : null
                                ]
                            }),
                            delayDuration: 100,
                            children: /*#__PURE__*/ _jsx("span", {
                                "aria-label": `${name}: ${valueText}${s?.note ? `, ${s.note}` : ''}`,
                                className: cn('flex aspect-square min-w-0 items-center justify-center rounded-[var(--radius-xs)] transition-transform hover:scale-105', specraFocusRing, !s && 'border border-[color:var(--border-subtle)]'),
                                "data-state": code,
                                "data-level": level,
                                role: "img",
                                style: {
                                    gridRow: row + 1,
                                    gridColumn: col + 1,
                                    background: fill
                                },
                                // biome-ignore lint/a11y/noNoninteractiveTabindex: focusable so its tooltip opens from the keyboard
                                tabIndex: 0,
                                children: /*#__PURE__*/ _jsx(SpecraText, {
                                    "aria-hidden": "true",
                                    className: cn('font-medium', strongInk ? 'text-[color:var(--chart-heat-ink-strong)]' : 'text-foreground', !s && 'text-muted-foreground'),
                                    size: "micro",
                                    children: code
                                })
                            })
                        }, code);
                    })
                })
            }),
            source || footnote ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: [
                    source,
                    footnote
                ].filter(Boolean).join(' · ')
            }) : null
        ]
    });
}
